import { Button } from "@package/subframe/components/Button";
import { IconWithLogoBackground } from "@package/subframe/components/IconWithLogoBackground";
import {
  FeatherArchive,
  FeatherPauseCircle,
  FeatherPlayCircle,
} from "@subframe/core";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { ChangeNurseryStatusDtoStatus } from "@/generated/model";
import { useChangeNurseryStatusCommandControllerChangeStatus } from "@/generated/nurseries/nurseries";

interface ChangeNurseryStatusDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  nursery: { id: string; name: string };
  targetStatus: ChangeNurseryStatusDtoStatus;
}

interface StatusChangeCopy {
  variant: "success" | "warning" | "error";
  icon: React.ReactNode;
  title: string;
  body: React.ReactNode;
  confirmLabel: string;
  pendingLabel: string;
  successMessage: string;
}

function getCopy(
  targetStatus: ChangeNurseryStatusDtoStatus,
  name: string,
): StatusChangeCopy {
  switch (targetStatus) {
    case ChangeNurseryStatusDtoStatus.DISABLED:
      return {
        variant: "warning",
        icon: <FeatherPauseCircle />,
        title: "Disable nursery?",
        body: (
          <>
            Are you sure you want to disable <strong>{name}</strong>? Staff will
            lose access until it is re-enabled.
          </>
        ),
        confirmLabel: "Yes, disable",
        pendingLabel: "Disabling...",
        successMessage: `"${name}" has been disabled`,
      };
    case ChangeNurseryStatusDtoStatus.ARCHIVED:
      return {
        variant: "error",
        icon: <FeatherArchive />,
        title: "Archive nursery?",
        body: (
          <>
            Archiving <strong>{name}</strong> is permanent and cannot be undone.
            Staff lose access immediately; all data is retained.
          </>
        ),
        confirmLabel: "Yes, archive",
        pendingLabel: "Archiving...",
        successMessage: `"${name}" has been archived`,
      };
    default:
      return {
        variant: "success",
        icon: <FeatherPlayCircle />,
        title: "Enable nursery?",
        body: (
          <>
            Are you sure you want to enable <strong>{name}</strong>? Staff will
            be able to access it again.
          </>
        ),
        confirmLabel: "Yes, enable",
        pendingLabel: "Enabling...",
        successMessage: `"${name}" has been enabled`,
      };
  }
}

export function ChangeNurseryStatusDialog({
  open,
  onOpenChange,
  nursery,
  targetStatus,
}: ChangeNurseryStatusDialogProps) {
  const queryClient = useQueryClient();
  const changeStatus = useChangeNurseryStatusCommandControllerChangeStatus();
  const copy = getCopy(targetStatus, nursery.name);

  const handleConfirm = async () => {
    try {
      await changeStatus.mutateAsync({
        id: nursery.id,
        data: { status: targetStatus },
      });
      toast.success(copy.successMessage);
      await queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey[0];
          return typeof key === "string" && key.includes("/nurseries");
        },
      });
      onOpenChange(false);
    } catch {
      toast.error(`Failed to update "${nursery.name}"`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton={false} className="max-w-[480px] p-0">
        <DialogTitle className="sr-only">{copy.title}</DialogTitle>
        <div className="flex h-full w-full flex-col items-center justify-center gap-4 bg-white">
          <div className="flex max-w-[448px] flex-col items-center gap-6 p-4">
            <div className="flex flex-col items-center gap-6">
              <IconWithLogoBackground
                variant={copy.variant}
                size="x-large"
                icon={copy.icon}
              />
              <div className="flex flex-col items-center gap-2">
                <span className="w-full text-center text-heading-2 font-heading-2 text-default-font">
                  {copy.title}
                </span>
                <span className="text-center text-body font-body text-subtext-color">
                  {copy.body}
                </span>
              </div>
            </div>
          </div>
          <div className="flex w-full flex-col items-center gap-3 bg-white px-4 py-4">
            <Button
              className="h-14 w-80 flex-none"
              variant="neutral-secondary"
              size="extra-large"
              onClick={() => onOpenChange(false)}
              disabled={changeStatus.isPending}
            >
              Cancel
            </Button>
            <Button
              className="h-14 w-80 flex-none"
              variant="neutral-primary"
              size="extra-large"
              onClick={handleConfirm}
              disabled={changeStatus.isPending}
            >
              {changeStatus.isPending ? copy.pendingLabel : copy.confirmLabel}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
